import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Template, TemplateApiService } from '../../core/template';
import { ConfirmationDialog, ErrorDialog } from '../dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorFactory } from '../../core/api-errors';
import { DeleteTemplateResponse } from '../../core/template/template';
import { TranslateService } from '@ngx-translate/core';
import { FormFields } from '../create-progress/form-fields';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { ProductAnalyticsService } from '@vendasta/product-analytics';

import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { AppDialogModule } from '../dialog';
import { SiteScreenShotComponent } from '../site-screen-shot';

@Component({
  selector: 'app-template-card',
  templateUrl: './template-card.component.html',
  styleUrls: ['./template-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    MatCardModule,
    TranslateModule,
    SiteScreenShotComponent,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    AppDialogModule,
    MatProgressSpinnerModule,
    MatInputModule,
    FormsModule,
    GalaxyPopoverModule,
  ],
})
export class TemplateCardComponent implements OnInit {
  @Input() template: Template;
  @Input() canEdit: boolean;
  @Input() canDelete: boolean;
  @Input() partnerId: string;
  @Input() icon: string;
  @Input() templateSelect: boolean;
  @Input() isLessMaxTemplates: boolean;
  @Input() set templateCreatingIds(templateDuplicatingIds) {
    this.isCreating = templateDuplicatingIds.includes(this.template.siteId);
  }
  @Input() isStaging: boolean;

  @Output() selected = new EventEmitter<Template>();
  @Output() isDeleted = new EventEmitter<boolean>();
  @Output() duplicateTemplateButtonClicked = new EventEmitter<FormFields>();
  @Output() templateId = new EventEmitter<string>();
  ready = false;
  allowedToEdit = false;
  screenshotUrl: string;
  duplicationForm: FormFields = new FormFields();
  duplicateDialog: any;
  resetStagingDialog: any;
  duplicateVersion: string;
  isLoadingTemplate: boolean;
  duplicateButtonDisabled: boolean;
  maxLength = 50;
  duplicateTitleSuggestion = ': copy';
  PopoverPositions = PopoverPositions;
  showPopover = false;
  duplicatePopoverText: string;
  public ecommerceSubtitle: string;
  isCreating: boolean;
  useTemplateButton: string;

  @ViewChild('callDuplicateDialog') callDuplicateDialog: TemplateRef<any>;

  constructor(
    public dialog: MatDialog,
    private templateService: TemplateApiService,
    private apiErrorFactory: ApiErrorFactory,
    private translateService: TranslateService,
    private snowplow: ProductAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.ready = true;
    this.allowedToEdit = this.getAllowedToEdit();
    this.screenshotUrl = this.getScreenshotUrl();
    this.ecommerceSubtitle = this.translateService.instant(
      'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SECTIONS.ECOMMERCE.SUBTITLE_PLAINTEXT',
    );
    this.useTemplateButton = this.isStaging
      ? this.translateService.instant('PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.RESET_FROM_TEMPLATE')
      : this.translateService.instant('PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.SELECT_TEMPLATE');
  }

  selectTemplate(): void {
    if (this.isStaging) {
      this.dialog
        .open(ConfirmationDialog, {
          data: {
            content: 'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.CONFIRM_RESET_TEMPLATE.TITLE',
            subtitle: 'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.CONFIRM_RESET_TEMPLATE.BODY',
            action: 'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.CONFIRM_RESET_TEMPLATE.CONFIRM',
          },
          disableClose: false,
          hasBackdrop: true,
        })
        .afterClosed()
        .subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.selected.emit(this.template);
          }
        });
    } else {
      if (this.canEdit) {
        this.editTemplate();
      } else {
        this.dialog
          .open(ConfirmationDialog, {
            data: {
              content: 'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.CONFIRMATION.TITLE',
              subtitle: 'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.CONFIRMATION.BODY',
              action: 'PAGES.CREATE_FROM_TEMPLATES.TEMPLATE_SELECTION.CONFIRMATION.CONFIRM',
            },
            disableClose: false,
            hasBackdrop: true,
          })
          .afterClosed()
          .subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.selected.emit(this.template);
            }
          });
      }
    }
  }

  getAllowedToEdit(): boolean {
    return this.template.partnerId === this.partnerId;
  }

  navigateToTemplate(): void {
    window.open(`https://${this.template.domain}`);
  }

  editTemplate(): void {
    window.open(`https://${window.location.hostname}/template/${this.template.siteId}/overview`);
  }

  getScreenshotUrl(): string {
    let screenshot = this.template.screenshotUrl;
    // remove once handled by backend
    if (!!screenshot && !this.template.screenshotUrl.endsWith('-1000x1050')) {
      screenshot = screenshot + '-1000x1050';
    }
    return screenshot;
  }

  showDeleteTemplateDialog(): void {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          content: 'PAGES.DELETE_TEMPLATE.CONTENT',
          action: 'PAGES.DELETE_TEMPLATE.ACTION',
        },
        disableClose: false,
        hasBackdrop: true,
      })
      .afterClosed()
      .subscribe((shouldDelete: boolean) => {
        if (shouldDelete) {
          this.templateService.deleteTemplate(this.template.siteId).subscribe(
            (response: DeleteTemplateResponse) => {
              this.hideTemplateCard(response.siteId);
            },
            (err) => this.handleError(err),
          );
        }
      });
  }

  formatNewTemplateTitle(title: string, duplicateVersion: string = this.duplicateVersion): string {
    title = title
      .slice(
        0,
        title.lastIndexOf(this.duplicateTitleSuggestion) !== -1
          ? title.lastIndexOf(this.duplicateTitleSuggestion)
          : undefined,
      )
      .slice(0, 35);
    return `${title}${this.duplicateTitleSuggestion}-${duplicateVersion.slice(1, -1)}`;
  }

  formatNewTemplateDomain(title: string, duplicateVersion: string = this.duplicateVersion): string {
    const domain = title
      .slice(
        0,
        title.lastIndexOf(this.duplicateTitleSuggestion) !== -1
          ? title.lastIndexOf(this.duplicateTitleSuggestion)
          : undefined,
      )
      .slice(0, this.maxLength)
      .replace(/[^a-zA-Z0-9]/gm, '')
      .replace(/[A-Z]/gm, (letter) => letter.toLowerCase());
    return domain + '-' + this.duplicateTitleSuggestion.slice(2) + '-' + duplicateVersion;
  }

  showDuplicateTemplateDialog(): void {
    this.duplicateVersion = new Date().getTime().toString(36);
    this.duplicationForm.siteTitle = this.formatNewTemplateTitle(this.template.title);
    this.duplicateButtonDisabled = !this.template.templateVersion;
    this.duplicateDialog = this.dialog.open(this.callDuplicateDialog);
    this.templateId.emit(this.template.siteId);
  }

  duplicateTemplate(): void {
    this.duplicationForm.siteTitle = this.duplicationForm.siteTitle.trimStart().trim();
    this.duplicateDialog.close();
    this.duplicationForm.defaultDomain = this.formatNewTemplateDomain(this.duplicationForm.siteTitle);
    this.duplicateTemplateButtonClicked.emit(this.duplicationForm);
  }

  calculateRemainingChars(currentChars: number): number {
    return this.maxLength - currentChars;
  }

  private hideTemplateCard(siteId: string): void {
    if (siteId === this.template.siteId) {
      this.isDeleted.emit(true);
    }
  }

  private handleError(errorResponse: HttpErrorResponse): void {
    const parsedError = this.apiErrorFactory.parseErrorResponse(errorResponse);
    this.displayError(parsedError.getUserMessage());
  }

  private displayError(error: string): void {
    this.dialog.open(ErrorDialog, {
      data: { errMsg: error },
    });
  }

  showPopovers(): void {
    window.setTimeout(() => {
      this.showPopover = true;
      this.duplicatePopoverText = !this.isLessMaxTemplates
        ? this.translateService.instant('PAGES.CREATE_FROM_TEMPLATES.DUPLICATE_DIALOG.POPOVER_MAX_TEMPLATES')
        : !this.template.templateVersion
          ? this.translateService.instant('PAGES.CREATE_FROM_TEMPLATES.DUPLICATE_DIALOG.POPOVER_UNPUBLISHED')
          : this.translateService.instant('PAGES.CREATE_FROM_TEMPLATES.DUPLICATE_DIALOG.POPOVER_EMPTY_TITLE');
    }, 400);
  }
  hidePopovers(): void {
    this.showPopover = false;
  }
}
